import React, { Component, ContextType, createRef } from 'react';
import ApiUrls from '@api/apiUrls';
import { fetchApi } from '@api/fetchApi';
import { IApiCopyType } from '@api/interfaces/data-contracts';
import { apiInvestorAccount } from '@api/services/investorAccount.apiService';
import { apiUserCopyTrading } from '@api/services/user/copyTrading/userCopyTrading.apiService';
import { IApiGridResponse } from '@api/types';
import { WarningMessage } from '@components-library/WarningMessage/WarningMessage';
import { AccountCreateButtons } from '@components-member/TradingAccounts/AccountCreate/components/AccountCreateButtons/AccountCreateButtons';
import { BeforeActionsContent } from '@components-member/TradingAccounts/AccountCreate/components/BeforeActionContent';
import { AccountCreateContext } from '@components-member/TradingAccounts/AccountCreate/context';
import {
  ACCOUNT_CREATE_ACTION_TYPE_ENUM,
  EnumUTAFormTAField,
} from '@components-member/TradingAccounts/AccountCreate/enums';
import { getFilterParams } from '@components-member/TradingAccounts/AccountCreate/getFilterParams';
import { setIbCodeTooltip } from '@components-member/TradingAccounts/AccountCreate/helpers';
import { IBaseAccountCreateWithContext } from '@components-member/TradingAccounts/AccountCreate/interfaces';
import { getSocialTradingTypeOption } from '@components-member/TradingAccounts/AccountCreate/socialTrading/helpers';
import { lotFieldDefaultValue } from '@components-member/shared/forms/formFields';
import { configureAccountSettings } from '@components-member/socialTrading/shared/accountSettingsService';
import * as SOCIAL_CONSTANTS from '@components-member/socialTrading/shared/constants';
import {
  emptyInvestorWarnings,
  emptySelectedMasterAccount,
} from '@components-member/socialTrading/shared/initialEmptyState';
import { createInvestorAccount, getCopyTradingSettings } from '@components-member/socialTrading/shared/requests';
import { setMasterAdditionalInfo } from '@components-member/socialTrading/shared/selectMasterComponents/setMasterAccountComponents';
import { getOptions } from '@components-member/socialTrading/shared/selectMasterComponents/utils';
import { IMaster, IModalFormField } from '@components-member/socialTrading/social.interfaces';
import {
  copyTypeOptionsHandler,
  filterIbCodeFieldIfDisabled,
  getLeverageFromInvestorType,
  getMasterCopyType,
  getMasterDescription,
  setLotForEquityAndBalanceCopyTypeToZero,
  setMarginPercentageLevelToZero,
} from '@components-member/socialTrading/utils';
import { FormUtils } from '@components-shared/forms/forms.utils';
import { pushAccountCreateErrorEvent } from '@components-shared/googleAnalyticsEvents';
import { NotificationDialog } from '@components-shared/modal';
import Preloader from '@components-shared/preloader';
import { EmptyObject } from '@components-shared/shared.interfaces';
import { EnumUSTFormField } from '@components-shared/socialTrading/enums';
import {
  isLotCopyTypeCheck,
  minDepositLimitsAreConfigured,
  leverageFieldHandler,
} from '@components-shared/socialTrading/utils';
import { keyMap } from '@locales/lokalise/keyMap';
import { handleError } from '@utils/errors';
import { getInitialState } from 'components/shared/forms/formGenerator';
import { connectToContext } from 'contextStore';
import { TRADING_ACCOUNT_SOURCE } from 'global.enums';
import tradingAccountFormStyles from '../tradingAccountForm.styles.module.scss';
import { INVESTOR_ACCOUNT_FIELDS, InvestorAccountForm } from './constants';
import { IInvestorAccountCreateState } from './interfaces';

class InvestorAccountCreate extends Component<IBaseAccountCreateWithContext, IInvestorAccountCreateState> {
  static contextType = AccountCreateContext;

  // @ts-ignore declare cannot be used at the current moment
  context!: ContextType<typeof AccountCreateContext>;

  INVESTOR_FIELDS = [...INVESTOR_ACCOUNT_FIELDS];

  formRef = createRef<{
    update: (data: IModalFormField[]) => void;
    validate: () => boolean;
  }>();

  period = SOCIAL_CONSTANTS.DEFAULT_RATING_PERIOD;

  state: IInvestorAccountCreateState = {
    processing: true,
    isCreatingTradingAccount: false,
    agree: false,
    form: { ...getInitialState(this.INVESTOR_FIELDS) },
    numberMasterAccounts: 50,
    selectedMaster: emptySelectedMasterAccount,
    masterAccounts: [],
    recommendedMinDepositLimits: [],
    investorWarnings: emptyInvestorWarnings,
    isUpdateInvestorLeverageAllowed: false,
    selectedAccountDescription: null,
  };

  async componentDidMount(): Promise<void> {
    await this.getInitialData();
  }

  async componentDidUpdate(
    _prevProps: Readonly<IBaseAccountCreateWithContext>,
    prevState: Readonly<IInvestorAccountCreateState>,
  ) {
    if (prevState.masterAccounts !== this.state.masterAccounts) {
      this.setMasterAccountOptions();

      this.updateForm();
    }

    if (prevState.form.masterAccountId !== this.state.form.masterAccountId) {
      const availableAllocationMethods = await this.handleAccountSettings(Number(this.state.form.masterAccountId));

      this.masterAccountSelectionHandler(this.state.form.masterAccountId, availableAllocationMethods ?? []);
    }
  }

  componentWillUnmount(): void {
    this.setState({ selectedMaster: emptySelectedMasterAccount });
    this.INVESTOR_FIELDS = [...INVESTOR_ACCOUNT_FIELDS];

    this.updateForm();
  }

  getInitialData = async (): Promise<void> => {
    const { updateActionState, formOpeningSource } = this.context;

    try {
      await this.getMasterAccounts('');

      const socialTradingSettings = await getCopyTradingSettings();

      if (formOpeningSource === TRADING_ACCOUNT_SOURCE.SIDEBAR) {
        const investorTypes = (await apiInvestorAccount.getTypes()).data;

        if (investorTypes) {
          updateActionState({
            type: ACCOUNT_CREATE_ACTION_TYPE_ENUM.INVESTOR_TRADING_TYPES,
            payload: investorTypes,
          });
        }
      }

      const { investorWarnings, recommendedMinDepositLimits, isUpdateInvestorLeverageAllowed } =
        socialTradingSettings.data;

      this.setState(
        {
          processing: false,
          recommendedMinDepositLimits,
          investorWarnings,
          isUpdateInvestorLeverageAllowed,
        },
        this.formInitializer,
      );
    } catch (error) {
      handleError(keyMap.tradingAccountsFailedToFetchTradingAccounts, error);
    }
  };

  getMasterAccounts = async (filterKeyword = '') => {
    const { numberMasterAccounts } = this.state;
    const { accountCreateState, selectedPlatformType } = this.context;

    const { investorTrading } = accountCreateState;

    const filterProps = getFilterParams({
      filterKeyword,
      maxAccounts: numberMasterAccounts,
      investorTypes: investorTrading,
      tradingPlatformType: selectedPlatformType === 'None' ? undefined : selectedPlatformType,
    });

    try {
      const response = await fetchApi.post<IApiGridResponse<IMaster>>(
        ApiUrls.accountType.searchMastersRatingInfo,
        filterProps,
        {
          params: { period: this.period },
        },
      );

      const masterAccounts = response.data.records;

      this.setState({
        masterAccounts,
      });
    } catch (error) {
      handleError(keyMap.tradingAccountsFailedToFetchTradingAccounts, error);
    }
  };

  formInitializer = (): void => {
    const { canSetIb, clientSourceType, ibCodePrefix } = this.props;

    const { accountCreateState, selectedPlatformType } = this.context;

    const { investorTrading } = accountCreateState;

    this.INVESTOR_FIELDS = filterIbCodeFieldIfDisabled(this.INVESTOR_FIELDS, canSetIb, clientSourceType);
    getSocialTradingTypeOption(investorTrading, this.INVESTOR_FIELDS, selectedPlatformType);
    setIbCodeTooltip({ fields: this.INVESTOR_FIELDS, canSetIb, clientSourceType, ibCodePrefix });
    this.setMasterAccountOptions();

    this.updateForm();
  };

  setMasterAccountOptions = (): void => {
    const { accountCreateState } = this.context;
    const { masterAccounts } = this.state;

    const { investorTrading } = accountCreateState;

    const isLimit = investorTrading.length === 0;

    const masterAccountIdIdx = FormUtils.getFieldIndex(this.INVESTOR_FIELDS, EnumUSTFormField.masterAccountId);
    const optionComponent = getOptions(masterAccounts);

    if (masterAccountIdIdx > -1) {
      FormUtils.updateFormField(
        masterAccountIdIdx,
        {
          options: optionComponent,
          attributes: { disabled: isLimit },
        },
        this.INVESTOR_FIELDS,
      );

      this.updateForm();
    }
  };

  handleAccountSettings = async (value: number) => {
    const { availableAllocationMethods } = await configureAccountSettings({
      value,
      fields: this.INVESTOR_FIELDS,
      form: this.formRef,
      onChange: this.onChange,
      fetchSettings: () => apiUserCopyTrading.getInvestorSettings({ masterAccountId: value, investorId: null }),
    });

    copyTypeOptionsHandler(availableAllocationMethods, this.INVESTOR_FIELDS);

    return availableAllocationMethods;
  };

  masterAccountSelectionHandler = (id: string, availableAllocationMethods: IApiCopyType[]) => {
    const { masterAccounts, recommendedMinDepositLimits, investorWarnings, isUpdateInvestorLeverageAllowed } =
      this.state;

    const masterAccountIdIdx = FormUtils.getFieldIndex(this.INVESTOR_FIELDS, EnumUSTFormField.masterAccountId);

    const selectedMasterAccount = masterAccounts.find((account) => account.masterAccountId === id);

    if (!selectedMasterAccount) {
      return;
    }

    const masterCopyType = getMasterCopyType(availableAllocationMethods, selectedMasterAccount.copyType);

    const investorType = this.setAccountInvestorTypes(selectedMasterAccount);

    this.setState({
      form: {
        masterAccountId: selectedMasterAccount.masterAccountId,
        type: investorType,
        copyType: masterCopyType,
        lot: lotFieldDefaultValue,
        leverage: selectedMasterAccount.leverage,
        ibCode: '',
        marginStopCopyLevel: '',
        stopCopyViaMarginLevel: '',
        percentageLossStopCopyLevel: '',
      },
      selectedMaster: selectedMasterAccount,
    });

    const leverages = getLeverageFromInvestorType(
      this.context.accountCreateState[ACCOUNT_CREATE_ACTION_TYPE_ENUM.INVESTOR_TRADING_TYPES],
      investorType,
    );

    leverageFieldHandler(
      selectedMasterAccount.leverage,
      this.INVESTOR_FIELDS,
      isUpdateInvestorLeverageAllowed,
      leverages,
    );

    this.lotFieldHandler(selectedMasterAccount.copyType);

    const description = getMasterDescription(
      selectedMasterAccount.recommendedMinDepositAmount,
      selectedMasterAccount.currency,
    );

    const showDescription = minDepositLimitsAreConfigured(
      recommendedMinDepositLimits,
      selectedMasterAccount.groupMappingType,
    );

    setMasterAdditionalInfo(
      masterAccountIdIdx,
      this.INVESTOR_FIELDS,
      selectedMasterAccount,
      description,
      investorWarnings,
      showDescription,
    );
  };

  lotFieldHandler = (copyType: IApiCopyType): void => {
    const lotFieldIdx = FormUtils.getFieldIndex(this.INVESTOR_FIELDS, EnumUSTFormField.lot);

    const isHidden = !isLotCopyTypeCheck(copyType);

    this.onChange(EnumUTAFormTAField.lot, lotFieldDefaultValue);

    FormUtils.updateFormField(lotFieldIdx, { isHidden }, this.INVESTOR_FIELDS);
  };

  setAccountInvestorTypes = (master: IMaster | EmptyObject): number | undefined => {
    const { investorTrading } = this.context.accountCreateState;

    const type = investorTrading.find((item) => item.id === master.investorTradingAccountTypeId);

    this.setState({
      selectedAccountDescription: type?.description ?? null,
    });

    if (type) {
      return type.id;
    }
  };

  renderBeforeActionContent = () => {
    const { selectedAccountDescription } = this.state;

    return (
      <BeforeActionsContent
        changeAgree={this.changeAgree}
        agree={this.state.agree}
        outerFooterChildren={
          selectedAccountDescription && (
            <WarningMessage
              className={tradingAccountFormStyles.noteMessage}
              title={keyMap.commonPleaseNote}
              type="warning">
              {selectedAccountDescription}
            </WarningMessage>
          )
        }
      />
    );
  };

  changeAgree = (): void => {
    const { agree } = this.state;

    this.setState({ agree: !agree });
  };

  createAccount = async (): Promise<void> => {
    const { refresh } = this.context;
    const isPreloaderEnable = true;

    const isValid = this.isValidForm();

    if (!isValid) {
      return;
    }

    const { selectedMaster, form } = this.state;
    const { user } = this.props;

    try {
      this.setState({ isCreatingTradingAccount: true });
      setLotForEquityAndBalanceCopyTypeToZero(form);
      setMarginPercentageLevelToZero(form);

      await createInvestorAccount(form, user, selectedMaster);

      NotificationDialog.show(keyMap.tradingAccountsAccountSuccessfullyCreated);
      this.setState({ isCreatingTradingAccount: false });
      this.props.closeHandler();

      refresh({ isPreloaderEnable });
    } catch (error) {
      this.setState({ isCreatingTradingAccount: false });
      pushAccountCreateErrorEvent(error.response.data);
      handleError(keyMap.tradingAccountsFailedToFetchTradingAccounts, error);
    }
  };

  updateForm = (): void => {
    if (this.formRef && this.formRef.current) {
      this.formRef.current.update(this.INVESTOR_FIELDS);
    }
  };

  isValidForm = (): boolean | undefined => {
    if (this.formRef && this.formRef.current) {
      return this.formRef.current.validate();
    }
  };

  onChange = (name: string, value: unknown): void => {
    this.setState((prevState) => ({
      form: {
        ...prevState.form,
        [name]: value,
      },
    }));
  };

  render() {
    const { isAccountLimitForUnverifiedUsers } = this.context;
    const { processing, form, agree, isCreatingTradingAccount } = this.state;
    const { changeStep, isBackButtonDisplayed } = this.props;

    if (processing) {
      return <Preloader />;
    }

    const disabled = !agree || isAccountLimitForUnverifiedUsers || isCreatingTradingAccount;

    return (
      <InvestorAccountForm
        className="grid--form"
        form={form}
        ref={this.formRef}
        beforeActions={this.renderBeforeActionContent()}
        getRequestData={this.getMasterAccounts}
        onChange={this.onChange}
        lotFieldHandler={this.lotFieldHandler}
        after={
          <AccountCreateButtons
            isCreatingTradingAccount={isCreatingTradingAccount}
            createAction={this.createAccount}
            disabled={disabled}
            changeStepHandler={changeStep}
            isBackButtonDisplayed={isBackButtonDisplayed}
          />
        }
      />
    );
  }
}

const InvestorAccountCreateWithContext = connectToContext(InvestorAccountCreate);

export { InvestorAccountCreateWithContext as InvestorAccountCreate };
